import { Component, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { RouterLink } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { AsmService } from '../../services/asm/asm.service';
import { TranslocoModule } from '@ngneat/transloco';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, takeUntil } from 'rxjs';
import { UserService } from '../../core/user/user.service';
import { User } from '../../core/user/user.types';
import { ScanHistory } from '../../interfaces/asm/models';
import { ScanStatusResponse } from '../../interfaces/asm/ScanStatusResponse';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'app-a-quick-scan-history',
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatInputModule,
        MatCardModule,
        MatMenuModule,
        MatButtonModule,
        RouterLink,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        MatTooltipModule,
        TranslocoModule,
        MatProgressSpinnerModule,
        DatePipe
    ],
    templateUrl: './a-quick-scan-history.component.html',
    styleUrl: './a-quick-scan-history.component.scss',
})
export class AQuickScanHistoryComponent {
    displayedColumns: string[] = [
        'domainName',
        'summary',
        'scanEngineUsed',
        'lastScan',
        'initiatedBy',
        'status',
        'action',
    ];
    data: ScanHistory[] = [];
    dataSource: MatTableDataSource<ScanHistory>;
    totalItems = 0;
    pageSizes = [10, 20, 50, 1000];
    isLoadingResults = true;
    isRateLimitReached = false;

    user: User;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    // isToggled
    isToggled = false;

    constructor(
        public themeService: CustomizerSettingsService,
        private _userService: UserService,
        private asmService: AsmService
    ) {
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to the user service
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;

                this.asmService
                    .getScanStatus(this.user.slug)
                    .subscribe((response: ScanStatusResponse) => {
                        this.isLoadingResults = false;
                        this.data.push(...response.scans.pending);
                        this.data.push(...response.scans.scanning);
                        this.data.push(...response.scans.completed);
                        this.totalItems =
                            response.scans.pending.length +
                            response.scans.scanning.length +
                            response.scans.completed.length;

                        // Assign the data to the data source for the table to render
                        this.dataSource = new MatTableDataSource(this.data);
                        this.dataSource.paginator = this.paginator;
                        this.dataSource.sort = this.sort;
                    });
            });
    }

    /**
     * After View Init
     */
    ngAfterViewInit() {}

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    // RTL Mode
    toggleRTLEnabledTheme() {
        this.themeService.toggleRTLEnabledTheme();
    }

    // Search Filter
    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    // onKeydown
    onKeydown(event: Event) {
        //console.log(event);
        return false;
    }

    // Placeholder for a proper time-ago pipe
    formatDateHumanized(date: string | Date | null | undefined): string {
        if (!date) return ''; // Or 'Never'
        // Use a library like 'ngx-moment' or 'date-fns' for 'time ago' formatting
        // Simple placeholder:
        const inputDate = new Date(date);
        const now = new Date();
        const diffSeconds = Math.round(
            (now.getTime() - inputDate.getTime()) / 1000
        );

        if (diffSeconds < 60) return `${diffSeconds} seconds ago`;
        const diffMinutes = Math.round(diffSeconds / 60);
        if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
        const diffHours = Math.round(diffMinutes / 60);
        if (diffHours < 24) return `${diffHours} hours ago`;
        const diffDays = Math.round(diffHours / 24);
        return `${diffDays} days ago`;
    }
}
